import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { PAYMENT_METHOD_QUERY } from '@/gql/queries';
import { useTheme, DefaultTheme } from 'styled-components';
import { Button, SvgAlertOutlined, SvgAttentionOutlined } from '@devsbb/ui';
import { useTranslation } from 'react-i18next';
import Markdown from 'markdown-to-jsx';
import { tk } from '@/i18n/translationKeys';
import { TrackYourPaymentsEvents } from '@/analytics';
import { useScreenMatch } from '@/hooks/useScreenMatch';
import { useApplicationData } from '@/providers/applicationData';
import { Group } from '@/components/Group';
import { Text } from '@/components/Text';
import { SubscriptionPayment } from '@/generated/types';
import { USER_PAYMENT_METHODS } from '@/gql/queries/paymentMethods';
import { QueryParamName } from '@/constants';
import { Root, ViewDetailsButton } from './FailedBanner.styles';
import { isPaymentMethodActive } from '@/utils/paymentMethods';

export type BannerVariant = 'error' | 'warning';

interface FailedBannerProps {
  totalCount: number;
  totalPrice: string | null;
  paymentsList: SubscriptionPayment[];
  variant: BannerVariant;
}

export const FailedBanner = ({
  totalCount,
  totalPrice,
  paymentsList,
  variant,
}: FailedBannerProps) => {
  const { t } = useTranslation();
  const { userId, storeCode, routeBuilder } = useApplicationData();
  const { isMobile } = useScreenMatch();
  const theme = useTheme();
  const [shouldCheckValidPaymentMethods, setShouldCheckValidPaymentMethods] = useState(false);

  const {
    isMultipleContracts,
    isMultiplePaymentMethods,
    singleContractName,
    singleContractId,
    singlePaymentMethodId,
  } = useMemo(() => {
    const hasMultiple = (data: SubscriptionPayment[], key: string): boolean => {
      const seen = new Set();

      for (const item of data) {
        let value: string | undefined;

        switch (key) {
          case 'paymentMethod':
            value = item.contract?.paymentMethod?.id;
            break;
          case 'contract':
            value = item.contract?.id;
            break;
        }

        if (value) {
          seen.add(value);
          if (seen.size > 1) return true;
        }
      }

      return false;
    };

    return {
      isMultipleContracts: hasMultiple(paymentsList, 'contract'),
      isMultiplePaymentMethods: hasMultiple(paymentsList, 'paymentMethod'),
      singleContractName:
        paymentsList[0]?.contract?.__typename === 'Flex' ? paymentsList[0]?.contract?.name : '',
      singleContractId: paymentsList[0]?.contract?.id ?? '',
      singlePaymentMethodId: paymentsList[0]?.contract?.paymentMethod?.id ?? '',
    };
  }, [paymentsList]);

  const { data: paymentMethodData, loading: isPaymentMethodLoading } = useQuery(
    PAYMENT_METHOD_QUERY,
    {
      variables: { id: singlePaymentMethodId },
      skip: isMultiplePaymentMethods,
      fetchPolicy: 'cache-and-network',
    }
  );

  const paymentMethodId = paymentMethodData?.paymentMethod?.id;
  const paymentMethodStatus = paymentMethodData?.paymentMethod?.status;
  const isActive = isPaymentMethodActive(paymentMethodStatus);

  const {
    data: validPaymentMethodsData,
    loading: isValidPaymentMethodsLoading,
    error: validPaymentMethodsError,
  } = useQuery(USER_PAYMENT_METHODS, {
    variables: { filter: { includeInvalid: false } },
    skip: !shouldCheckValidPaymentMethods,
    fetchPolicy: 'cache-and-network',
  });

  const handleViewDetailsClick = () => {
    TrackYourPaymentsEvents.failedBannerViewDetailsClick({
      userId,
      store: storeCode,
      isMultipleContracts,
      isMultiplePaymentMethods,
    });

    if (isMultipleContracts) {
      if (isMultiplePaymentMethods) {
        return (location.href = routeBuilder.buildSubscriptionDetailsRoute());
      }

      location.href = routeBuilder.buildPaymentMethodDetailsRoute({
        paymentMethodId: paymentMethodId ?? '',
        query: { [QueryParamName.retry]: true },
      });
      return;
    }
    return (location.href = routeBuilder.buildSubscriptionDetailsRoute({
      subscriptionId: singleContractId,
    }));
  };

  useEffect(() => {
    if (!shouldCheckValidPaymentMethods || isValidPaymentMethodsLoading) {
      return;
    }
    setShouldCheckValidPaymentMethods(false);
    const validPaymentMethodLength = validPaymentMethodsData?.paymentMethods?.length;

    if (validPaymentMethodsError || validPaymentMethodLength === undefined) {
      location.href = routeBuilder.buildPaymentMethodListRoute({
        query: { [QueryParamName.retry]: true },
      });
      return;
    }

    if (validPaymentMethodLength > 0) {
      if (isMultipleContracts) {
        location.href = routeBuilder.buildPaymentMethodBulkUpdateRoute({
          paymentMethodId: paymentMethodId,
          query: { [QueryParamName.retry]: true },
        });
        return;
      }

      location.href = routeBuilder.buildChangePaymentMethodRoute({
        subscriptionId: singleContractId,
        query: {
          [QueryParamName.currentPaymentMethodId]: paymentMethodId,
          [QueryParamName.retry]: true,
        },
      });
      return;
    } else {
      location.href = routeBuilder.buildAddPaymentMethodRoute({
        query: isMultipleContracts
          ? {
              [QueryParamName.bulkId]: paymentMethodId,
              [QueryParamName.retry]: true,
            }
          : {
              [QueryParamName.subscriptionId]: singleContractId,
              [QueryParamName.currentPaymentMethodId]: paymentMethodId,
              [QueryParamName.retry]: true,
            },
      });
      return;
    }
  }, [
    shouldCheckValidPaymentMethods,
    isValidPaymentMethodsLoading,
    validPaymentMethodsData,
    validPaymentMethodsError,
    isMultipleContracts,
    singleContractId,
    routeBuilder,
  ]);

  const handleChangeClick = () => {
    TrackYourPaymentsEvents.failedBannerChangePaymentMethodClick({
      userId,
      store: storeCode,
      isMultipleContracts,
      isMultiplePaymentMethods,
    });

    if (isMultiplePaymentMethods || !paymentMethodId) {
      location.href = routeBuilder.buildPaymentMethodListRoute({
        query: { [QueryParamName.retry]: true },
      });
      return;
    }

    if (isActive) {
      location.href = routeBuilder.buildPaymentIssueNoticeRoute({
        query: isMultipleContracts
          ? {
              [QueryParamName.bulkId]: paymentMethodId,
              [QueryParamName.retry]: true,
            }
          : {
              [QueryParamName.subscriptionId]: singleContractId,
              [QueryParamName.currentPaymentMethodId]: paymentMethodId,
              [QueryParamName.retry]: true,
            },
      });
      return;
    }

    if (!shouldCheckValidPaymentMethods) {
      setShouldCheckValidPaymentMethods(true);
    }
  };

  const buttonGroup = (
    <Group gap={4} mobileGap={4} fullWidth={isMobile}>
      <Button
        size="S"
        variant="Primary"
        onClick={handleChangeClick}
        disabled={isValidPaymentMethodsLoading || isPaymentMethodLoading}
      >
        {t(tk.yourPaymentsProceedToPayButtonText)}
      </Button>
      <ViewDetailsButton size="S" variant="Primary" onClick={handleViewDetailsClick}>
        {t(tk.yourPaymentsViewDetailsButtonText)}
      </ViewDetailsButton>
    </Group>
  );

  const subtitleText = totalPrice
    ? t(tk.yourPaymentsFailedBannerSubtitle, { totalCount, totalPrice })
    : t(tk.yourPaymentsFailedBannerSubtitleWithoutTotalPrice, { totalCount });

  const bannerConfig = {
    error: {
      icon: <SvgAttentionOutlined />,
      title: t(tk.yourPaymentsFailedBannerTitle),
      contentText: (
        <>
          {subtitleText}&nbsp;
          {isMultipleContracts || !singleContractName ? (
            t(tk.yourPaymentsFailedBannerYourRentals)
          ) : (
            <b>{singleContractName}</b>
          )}
          .
        </>
      ),
      button: buttonGroup,
      backgroundColor: (theme: DefaultTheme) => theme.colors.background.lightRed,
    },
    warning: {
      icon: <SvgAlertOutlined />,
      title: t(tk.yourPaymentsPaymentInProgressBannerTitle),
      contentText: <Markdown>{t(tk.yourPaymentsPaymentInProgressBannerText)}</Markdown>,
      button: null,
      backgroundColor: (theme: DefaultTheme) => theme.colors.background.lightYellow,
    },
  };

  const config = bannerConfig[variant];

  return (
    <Root isMobile={isMobile} gap={2} mobileGap={2} bgColor={config.backgroundColor(theme)}>
      <span style={{ flexShrink: 0 }}>{config.icon}</span>
      <Group vertical gap={2} mobileGap={2}>
        <Text typography="SmallParagraph" bold color={theme.colors.text.darkerGrey}>
          {config.title}
        </Text>
        <Text typography="SmallParagraph" color={theme.colors.text.darkerGrey}>
          {config.contentText}
        </Text>
        {config.button}
      </Group>
    </Root>
  );
};
